import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save } from 'lucide-react';
import { useProductGroupForm } from '../../../../apps/hooks/productGroup/useProductGroupForm';
import { ROUTES } from '../../../../routes/routes';
import { ProductGroupTreeEditor } from '../components/tree/ProductGroupTreeEditor';

export default function ProductGroupEditPage() {
  const { groupId } = useParams<{ groupId?: string }>();
  const isEditMode = Boolean(groupId);
  const navigate = useNavigate();
  const {
    groupName,
    setGroupName,
    treeForm,
    setTreeForm,
    loading,
    saving,
    loadError,
    save,
    navigateAwayWithUnsavedChangesGuard,
  } = useProductGroupForm(groupId);

  if (loading) {
    return (
      <Container fluid className="py-3">
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="dark" className="mb-3" />
            <p className="text-muted mb-0">Loading group…</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container fluid className="py-3">
        <Card>
          <Card.Body>
            <Alert variant="danger">{loadError}</Alert>
            <Button variant="outline-dark" size="sm" onClick={() => navigate(ROUTES.catalog.productGroups.list)}>
              <ChevronLeft size={14} className="me-1" />
              Back to list
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h4 className="h2 mb-2">{isEditMode ? 'Edit Product Group' : 'New Product Group'}</h4>
          <div className="border rounded p-3 bg-light" style={{ maxWidth: 720 }}>
            <div className="fw-semibold small mb-2">Tips</div>
            <ul className="text-muted small mb-0 ps-3">
              <li>Each row represents one product variant.</li>
              <li>
                Add an attribute column for each level (e.g. <code>color</code>, then <code>size</code>).
              </li>
            </ul>
          </div>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigateAwayWithUnsavedChangesGuard(ROUTES.catalog.productGroups.list)}
          disabled={saving}
        >
          <ChevronLeft size={14} className="me-1" />
          Back
        </Button>
      </div>

      <Card>
        <Card.Header style={{ backgroundColor: '#000', color: '#fff' }}>
          <span className="fw-semibold">{isEditMode ? 'Edit Group Details' : 'Create New Group'}</span>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Group name</Form.Label>
            <Form.Control
              type="text"
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder="e.g. Over Glossy Gloss colors"
              disabled={saving}
            />
            <Form.Text className="text-muted">
              Use product's name (without attributes) for easier identification
            </Form.Text>
          </Form.Group>

          <ProductGroupTreeEditor form={treeForm} onChange={setTreeForm} disabled={saving} />
        </Card.Body>
        <Card.Footer className="d-flex justify-content-end gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => navigateAwayWithUnsavedChangesGuard(ROUTES.catalog.productGroups.list)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="dark" onClick={save} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} className="me-1" />
                {isEditMode ? 'Save changes' : 'Create group'}
              </>
            )}
          </Button>
        </Card.Footer>
      </Card>
    </Container>
  );
}
